import { Component, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { SearchService } from '../shared/searchservice';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { SessionService } from '../shared/sessionservice';
import { environment } from '../../environments/environment';

@Component({
    selector: 'app-search-component',
    templateUrl: './search.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchComponent {
  claimControlNumber: Number = 0;
  serviceResult: any;
  searchForm: UntypedFormGroup;
  isAdmin: boolean = false;
  constructor(private searchService: SearchService, private router: Router,
              @Inject(DOCUMENT) private document: Document,
              @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
              private sessionService: SessionService, private route: ActivatedRoute) {

  }

  ngOnInit() {
    this.sessionService.userOrg = String(this.oktaAuth.authStateManager.getAuthState()?.idToken?.claims.orgval ?? '');
    this.sessionService.userId = this.oktaAuth.authStateManager.getAuthState()?.idToken?.claims.email ?? '';

    // Set isAdmin based on userId from environment config (case-insensitive)
    this.isAdmin = environment.adminUsers
                              .map((u: string) => u.toLowerCase())
                              .includes(this.sessionService.userId.toLowerCase());

    this.route.params.subscribe((params: Params) => {
      this.serviceResult = this.route.snapshot.data['data'].result;
    });
    
    this.searchForm = new UntypedFormGroup({
      controlNumber: new UntypedFormControl(null), //note, can have up to 3 Constructor Params: default value, validators, AsyncValidators
      claimRefNumber: new UntypedFormControl(null),
      policyNumber: new UntypedFormControl(null),
      insuredName: new UntypedFormControl(null),
      contactFirstName: new UntypedFormControl(null),
      contactLastName: new UntypedFormControl(null),
      startDate: new UntypedFormControl(null),
      endDate: new UntypedFormControl(null),
      transmitted: new UntypedFormControl(null),
      received: new UntypedFormControl(null),
      saved: new UntypedFormControl(null),
      verified: new UntypedFormControl(null),
      approved: new UntypedFormControl(null),
      pending: new UntypedFormControl(null),
      rejected: new UntypedFormControl(null),
      finalized: new UntypedFormControl(null)
    })
  }

  reset() {
    this.searchForm.patchValue({
      controlNumber: null,
      claimRefNumber: null,
      policyNumber: null,
      insuredName: null,
      contactFirstName: null,
      contactLastName: null,
      startDate: null,
      endDate: null,
      transmitted: false,
      received: false,
      saved: false,
      verified: false,
      approved: false,
      pending: false,
      rejected: false,
      finalized: false
    });
  }

  onSubmit() {
    let claimSearch = {
      userId: this.sessionService.userId,
      userOrg: this.sessionService.userOrg,
      controlNumber: this.searchForm.get('controlNumber').value ?
        Number(this.searchForm.get('controlNumber').value) : 0,
      referenceNumber: this.searchForm.get('claimRefNumber').value ? this.searchForm.get('claimRefNumber').value : '',
      policyNumber: this.searchForm.get('policyNumber').value,
      insuredName: this.searchForm.get('insuredName').value,
      contactFirstName: this.searchForm.get('contactFirstName').value,
      contactLastName: this.searchForm.get('contactLastName').value,
      receivedDateStart: this.searchForm.get('startDate').value,
      receivedDateEnd: this.searchForm.get('endDate').value,
      transmitted: this.getTransmittedValue(this.searchForm.get('transmitted').value),
      status: this.getStatusSearch()
    };
    this.searchService.search(claimSearch).subscribe(response => {
      if ((<Response>response).result !== null) {
        sessionStorage.setItem('lastSearchResult', JSON.stringify(response));
        this.router.navigate(['/searchresult'], { state: { searchResult: response } });
      }
      else {
        console.log('No results');
      }
    });
  }

  searchReceived() {                    
    let claimSearch = {
      userId: this.sessionService.userId,
      userOrg: this.sessionService.userOrg,
      controlNumber: 0,
      referenceNumber: '',
      policyNumber: null,
      insuredName: null,
      contactFirstName: null,
      contactLastName: null,
      receivedDateStart: null,
      receivedDateEnd: null,
      transmitted: null,
      status: ['Received']
    };
    this.searchService.search(claimSearch).subscribe(response => {
      if ((<Response>response).result !== null) {
        sessionStorage.setItem('lastSearchResult', JSON.stringify(response));
        this.router.navigate(['/searchresult'], { state: { searchResult: response } });
      }
    });
  }

  searchSaved() {                    
    let claimSearch = {
      userId: this.sessionService.userId,
      userOrg: this.sessionService.userOrg,
      controlNumber: 0,
      referenceNumber: '',
      policyNumber: null,
      insuredName: null,
      contactFirstName: null,
      contactLastName: null,
      receivedDateStart: null,
      receivedDateEnd: null,
      transmitted: null,
      status: ['Saved']
    };
    this.searchService.search(claimSearch).subscribe(response => {
      if ((<Response>response).result !== null) {
        sessionStorage.setItem('lastSearchResult', JSON.stringify(response));
        this.router.navigate(['/searchresult'], { state: { searchResult: response } });
      }
    });
  }

  getTransmittedValue(value){
    if(value === null)
      return null;

    if(value === 'No')
      return false;

    if(value === 'Yes')
      return true;
  }

  getStatusSearch(): any[] {
    //console.log(this.searchForm.get('received'));
    let status = [];
    if (this.searchForm.get('received').value != null && this.searchForm.get('received').value)
      status.push('Received');
    if (this.searchForm.get('saved').value != null && this.searchForm.get('saved').value)
      status.push('Saved');
    if (this.searchForm.get('verified').value != null && this.searchForm.get('verified').value)
      status.push('Verified');
    if (this.searchForm.get('approved').value != null && this.searchForm.get('approved').value)
      status.push('Approved');
    if (this.searchForm.get('pending').value != null && this.searchForm.get('pending').value)
      status.push('Pending');
    if (this.searchForm.get('rejected').value != null && this.searchForm.get('rejected').value)
      status.push('Rejected');
    if (this.searchForm.get('finalized').value != null && this.searchForm.get('finalized').value)
      status.push('Finalized');

    if (status.length > 0)
      return status;
    else
      return null;
  }

  newclaim() {
    this.router.navigate(['/newclaim']);
  }

  admin() {
    // Navigate to admin page or perform admin action
    this.router.navigate(['/admin']);
  }
}

export class Response {
  result: any;
  errorMessage: string;
  timeGenerated: string;
}
